"use client";

import React, { useState } from "react";
import { 
  X, 
  Dumbbell, 
  Calendar, 
  UserCheck, 
  Activity, 
  CreditCard, 
  DollarSign, 
  FileText,
  AlertCircle
} from "lucide-react";
import { GymMemberLead } from "@/lib/demos/gymDb";
import { GYM_COACHES, GYM_TIERS } from "@/lib/demos/gymData";

interface GymStageModalProps {
  memberLead: GymMemberLead;
  targetStage: GymMemberLead["stage"];
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (options: any) => void;
}

export default function GymStageModal({
  memberLead,
  targetStage,
  isOpen,
  onClose,
  onConfirm,
}: GymStageModalProps) {
  // Stage 2: Tour Scheduled
  const [tourDate, setTourDate] = useState(
    memberLead.tour_date || "Tomorrow at 07:00 AM"
  );
  const [assignedCoach, setAssignedCoach] = useState(
    memberLead.assigned_coach || "Marcus Vance (Head of Strength)"
  );

  // Stage 3: Assessment Completed
  const [inbodyScore, setInbodyScore] = useState(
    memberLead.inbody_score || "SMM: 38.4kg, BF: 14.2%, InBody Score: 88/100"
  );

  // Stage 4 & 5: Membership & Payment
  const [membershipTier, setMembershipTier] = useState(
    memberLead.membership_tier || "Executive Performance"
  );
  const [annualDues, setAnnualDues] = useState(
    memberLead.annual_dues || 95000
  );
  const [lockerNumber, setLockerNumber] = useState("14-Executive");
  const [paymentMethod, setPaymentMethod] = useState(
    "HDFC Infinia Visa Infinite (POS Verified)"
  );

  // Stage 6: Cancelled
  const [cancellationReason, setCancellationReason] = useState(
    "Relocation / Schedule conflict"
  );

  if (!isOpen) return null;

  const handleTierChange = (newTier: string) => {
    setMembershipTier(newTier);
    if (memberLead.currency === "USD") {
      if (newTier.includes("Black")) setAnnualDues(1850);
      else if (newTier.includes("Performance")) setAnnualDues(1200);
      else setAnnualDues(820);
    } else {
      if (newTier.includes("Black")) setAnnualDues(145000);
      else if (newTier.includes("Performance")) setAnnualDues(95000);
      else setAnnualDues(65000);
    }
  };

  const handleConfirm = () => {
    onConfirm({
      stage: targetStage,
      tour_date: tourDate,
      assigned_coach: assignedCoach,
      inbody_score: inbodyScore,
      membership_tier: membershipTier,
      annual_dues: Number(annualDues),
      lockerNumber,
      paymentMethod,
      cancellationReason,
    });
  };

  const sym = memberLead.currency === "USD" ? "$" : "₹";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-in fade-in duration-200">
      <div className="bg-stone-950 border border-stone-800 rounded-3xl max-w-lg w-full p-6 sm:p-7 relative shadow-2xl text-stone-100 space-y-5">
        <button
          onClick={onClose}
          className="absolute top-5 right-5 text-stone-400 hover:text-white p-1.5 rounded-full hover:bg-stone-900 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Modal Header */}
        <div className="flex items-center gap-3 border-b border-stone-800 pb-4">
          <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400 shrink-0">
            <Dumbbell className="w-5 h-5" />
          </div>
          <div>
            <span className="text-[10px] font-bold text-amber-400 uppercase tracking-wider block">
              Stage Transition Automation
            </span>
            <h3 className="text-base sm:text-lg font-black text-white">
              Advance to {targetStage}
            </h3>
            <p className="text-xs text-stone-400">
              Member: <span className="text-white font-semibold">{memberLead.lead_name}</span> ({memberLead.email})
            </p>
          </div>
        </div>

        {/* STAGE 2: WORKOUT & TOUR SCHEDULED */}
        {targetStage === "Workout & Tour Scheduled" && (
          <div className="space-y-4 text-xs">
            <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-300">
              Advancing to this stage will automatically generate and email the official <strong>VIP 3-Day Guest Pass PDF</strong> with entry barcode to the prospect.
            </div>

            <div className="space-y-1">
              <label className="text-[11px] font-bold text-stone-300 uppercase tracking-wider flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-amber-400" />
                <span>Orientation &amp; Tour Date/Time *</span>
              </label>
              <input
                type="text"
                value={tourDate}
                onChange={(e) => setTourDate(e.target.value)}
                placeholder="e.g. 2026-09-04 at 07:30 AM"
                className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3.5 py-2 text-xs text-white focus:outline-none focus:border-amber-500"
              />
            </div>

            <div className="space-y-1">
              <label className="text-[11px] font-bold text-stone-300 uppercase tracking-wider flex items-center gap-1.5">
                <UserCheck className="w-3.5 h-3.5 text-amber-400" />
                <span>Assigned Master Coach *</span>
              </label>
              <select
                value={assignedCoach}
                onChange={(e) => setAssignedCoach(e.target.value)}
                className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3.5 py-2 text-xs text-white focus:outline-none focus:border-amber-500"
              >
                {GYM_COACHES.map((coach) => (
                  <option key={coach.id} value={`${coach.name} (${coach.title})`}>
                    {coach.name} — {coach.specialty}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* STAGE 3: ASSESSMENT COMPLETED */}
        {targetStage === "Assessment Completed" && (
          <div className="space-y-4 text-xs">
            <div className="p-3 rounded-xl bg-indigo-500/10 border border-indigo-500/20 text-indigo-300">
              Record the clinical InBody 770 body composition metrics to email the prospect their diagnostic report summary.
            </div>

            <div className="space-y-1">
              <label className="text-[11px] font-bold text-stone-300 uppercase tracking-wider flex items-center gap-1.5">
                <Activity className="w-3.5 h-3.5 text-indigo-400" />
                <span>InBody 770 Diagnostic Benchmarks *</span>
              </label>
              <input
                type="text"
                value={inbodyScore}
                onChange={(e) => setInbodyScore(e.target.value)}
                placeholder="e.g. SMM: 40.2kg, BF: 13.8%, VO2 Max: 52 ml/kg"
                className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3.5 py-2 text-xs text-white focus:outline-none focus:border-indigo-500"
              />
            </div>
          </div>
        )}

        {/* STAGE 4: MEMBERSHIP PROPOSED */}
        {targetStage === "Membership Proposed" && (
          <div className="space-y-4 text-xs">
            <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-300">
              Select the finalized membership tier and annual investment to dispatch the official enrollment proposal.
            </div>

            <div className="space-y-1">
              <label className="text-[11px] font-bold text-stone-300 uppercase tracking-wider">
                Proposed Membership Tier *
              </label>
              <select
                value={membershipTier}
                onChange={(e) => handleTierChange(e.target.value)}
                className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3.5 py-2 text-xs text-white focus:outline-none focus:border-amber-500"
              >
                {GYM_TIERS.map((tier) => (
                  <option key={tier.id} value={tier.name}>
                    {tier.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-[11px] font-bold text-stone-300 uppercase tracking-wider">
                Annual Dues ({memberLead.currency}) *
              </label>
              <input
                type="number"
                value={annualDues}
                onChange={(e) => setAnnualDues(Number(e.target.value))}
                className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3.5 py-2 text-xs text-white focus:outline-none focus:border-amber-500"
              />
            </div>
          </div>
        )}

        {/* STAGE 5: ACTIVE MEMBER (WON) */}
        {targetStage === "Active Member (Won)" && (
          <div className="space-y-3.5 text-xs">
            <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-300">
              Ratifying this membership will automatically generate and email the official <strong>Annual Membership Agreement &amp; Tax Invoice PDF</strong>.
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-stone-300 uppercase tracking-wider">
                  Tier *
                </label>
                <select
                  value={membershipTier}
                  onChange={(e) => handleTierChange(e.target.value)}
                  className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-emerald-500"
                >
                  {GYM_TIERS.map((tier) => (
                    <option key={tier.id} value={tier.name}>
                      {tier.name.split(" ")[0]}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-bold text-stone-300 uppercase tracking-wider">
                  Annual Dues Paid ({sym}) *
                </label>
                <input
                  type="number"
                  value={annualDues}
                  onChange={(e) => setAnnualDues(Number(e.target.value))}
                  className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-emerald-500"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-bold text-stone-300 uppercase tracking-wider">
                  Locker Suite Assigned
                </label>
                <input
                  type="text"
                  value={lockerNumber}
                  onChange={(e) => setLockerNumber(e.target.value)}
                  placeholder="e.g. 14-Executive"
                  className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-emerald-500"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-bold text-stone-300 uppercase tracking-wider">
                  Payment Mode *
                </label>
                <input
                  type="text"
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-emerald-500"
                />
              </div>
            </div>
          </div>
        )}

        {/* STAGE 6: CANCELLED / INACTIVE */}
        {targetStage === "Cancelled / Inactive" && (
          <div className="space-y-3 text-xs">
            <div className="p-3 rounded-xl bg-stone-900 border border-stone-800 text-stone-400">
              Provide an archiving reason. A courteous follow-up note will be sent.
            </div>

            <div className="space-y-1">
              <label className="text-[11px] font-bold text-stone-300 uppercase tracking-wider">
                Reason for Inactivity *
              </label>
              <select
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3.5 py-2 text-xs text-white focus:outline-none focus:border-stone-500"
              >
                <option value="Relocation / Schedule conflict">Relocation / Schedule conflict</option>
                <option value="Trial pass expired without conversion">Trial pass expired without conversion</option>
                <option value="Joined corporate fitness subsidiary">Joined corporate fitness subsidiary</option>
                <option value="Medical pause or surgery">Medical pause or surgery</option>
              </select>
            </div>
          </div>
        )}

        {/* Footer Actions */}
        <div className="pt-3 border-t border-stone-800 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-xs font-semibold text-stone-400 hover:text-white transition-colors"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleConfirm}
            className="px-6 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-stone-950 font-bold text-xs transition-colors shadow-md shadow-amber-500/20 flex items-center gap-1.5"
          >
            <span>Confirm &amp; Dispatch Email</span>
          </button>
        </div>
      </div>
    </div>
  );
}
